/**
 * REST storage provider → enterprise databases.
 *
 * This is the browser-side adapter for a thin backend service that fronts
 * SQL Server / PostgreSQL / Snowflake / Azure SQL. ALL credentials, connection
 * strings and PAT tokens live on that backend — the browser only ever sees the
 * base URL and an opaque, short-lived bearer token (supplied by the host).
 *
 * The expected REST contract is documented in docs/DEPLOYMENT.md and mirrored by
 * docs/DATABASE_SCHEMA.sql. Endpoints:
 *   GET    /combinations            → Combination[]
 *   PUT    /combinations/:id        → upsert
 *   DELETE /combinations/:id
 *   GET    /events                  → UsageEvent[]   (supports ?since= & ?limit=)
 *   POST   /events                  → append
 *   GET/PUT/DELETE /favorites[/:id]
 *   GET/PUT /config
 */

import type {
  Combination,
  ExtensionConfig,
  Favorite,
  UsageEvent,
} from "@/types";
import type { StorageProvider } from "./types";

export interface RestApiOptions {
  /** Base URL of the storage API. Empty = the extension's own origin (/api). */
  baseUrl: string;
  /** Returns a fresh bearer token; refreshed per-request so it can rotate. */
  getToken?: () => string | Promise<string> | undefined;
  /** Base name of the database table set, sent as X-SPC-Table. */
  table?: string;
  /** Extra headers, e.g. the X-DBX-* Databricks connection details. */
  extraHeaders?: Record<string, string>;
  /** Provider id shown in diagnostics ("rest" or "cloud"). */
  kind?: string;
  /** Cap on events fetched per page for incremental retrieval. */
  pageLimit?: number;
}

export class RestApiProvider implements StorageProvider {
  readonly kind: string;
  private readonly base: string;

  constructor(private readonly options: RestApiOptions) {
    this.kind = options.kind ?? "rest";
    // Default to the Pages Functions bundled with this deployment.
    this.base = (options.baseUrl || "/api").replace(/\/$/, "");
  }

  async init(): Promise<void> {
    // Health probe — a misconfigured/unreachable backend must fail HERE so the
    // storage factory can fall back to browser storage instead of the whole
    // extension erroring out on its first read.
    const token = await this.options.getToken?.();
    const res = await fetch(`${this.base}/health`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...this.options.extraHeaders,
      },
    });
    if (res.status === 404) return; // health endpoint optional on custom backends
    if (!res.ok) {
      throw new Error(`Storage API health check failed: ${res.status}`);
    }
    const body = (await res.json().catch(() => null)) as { configured?: boolean } | null;
    if (body?.configured === false) {
      throw new Error(
        "Storage API reachable, but its database credentials are not configured.",
      );
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const token = await this.options.getToken?.();
    const res = await fetch(`${this.base}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(this.options.table ? { "X-SPC-Table": this.options.table } : {}),
        ...this.options.extraHeaders,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      throw new Error(`REST ${method} ${path} failed: ${res.status} ${res.statusText}`);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  getCombinations(): Promise<Combination[]> {
    return this.request<Combination[]>("GET", "/combinations");
  }
  async putCombination(combination: Combination): Promise<void> {
    await this.request("PUT", `/combinations/${encodeURIComponent(combination.id)}`, combination);
  }
  async deleteCombination(id: string): Promise<void> {
    await this.request("DELETE", `/combinations/${encodeURIComponent(id)}`);
  }

  /** Incremental retrieval: pull recent events first, paged. */
  getUsageEvents(): Promise<UsageEvent[]> {
    const limit = this.options.pageLimit ?? 1000;
    return this.request<UsageEvent[]>("GET", `/events?limit=${limit}`);
  }
  async appendUsageEvent(event: UsageEvent): Promise<void> {
    await this.request("POST", "/events", event);
  }

  getFavorites(): Promise<Favorite[]> {
    return this.request<Favorite[]>("GET", "/favorites");
  }
  async putFavorite(favorite: Favorite): Promise<void> {
    await this.request("PUT", `/favorites/${encodeURIComponent(favorite.id)}`, favorite);
  }
  async deleteFavorite(id: string): Promise<void> {
    await this.request("DELETE", `/favorites/${encodeURIComponent(id)}`);
  }

  async getConfig(): Promise<Partial<ExtensionConfig> | null> {
    return this.request<Partial<ExtensionConfig> | null>("GET", "/config");
  }
  async putConfig(config: ExtensionConfig): Promise<void> {
    await this.request("PUT", "/config", config);
  }

  async clearAll(): Promise<void> {
    await this.request("POST", "/admin/reset");
  }
}
